import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { useEffect, useState } from "preact/hooks";
import { Button } from "../ui/button";
import { Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  PILL_FILLED_CLASS,
  PILL_OUTLINE_CLASS,
} from "./CancelSubscriptionDialog";
import { formatSubscriptionDateForDisplay } from "./formatters";
import { pauseUntilMonthsFromToday } from "./pause-dates";

export function PauseSubscriptionDialog({
  open,
  onOpenChange,
  onDismiss,
  onConfirmPauseMonths,
  allowedMonths,
  pauseIsPending,
  defaultMonths,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDismiss: () => void;
  onConfirmPauseMonths: (months: number) => void;
  allowedMonths: number[];
  pauseIsPending: boolean;
  /** Preferred default selection when the dialog opens. Falls back to 1, then the first allowed option. */
  defaultMonths?: number;
}) {
  const [selectedMonths, setSelectedMonths] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      const preferred =
        defaultMonths != null && allowedMonths.includes(defaultMonths)
          ? defaultMonths
          : allowedMonths.includes(1)
            ? 1
            : (allowedMonths[0] ?? null);
      setSelectedMonths(preferred);
    }
  }, [open, allowedMonths, defaultMonths]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        onOpenChange={onOpenChange}
        overlayClassName="tw:z-70"
        className="tw:z-71 tw:p-4"
      >
        <DialogHeader>
          <div className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-2 tw:w-full">
            <DialogTitle
              className="tw:min-w-0 tw:flex-1 tw:pr-1 tw:text-left"
              style={{ color: "var(--so-text, #523D33)" }}
            >
              Pause
            </DialogTitle>
            <DialogClose
              data-slot="dialog-close"
              render={
                <Button
                  variant="ghost"
                  className="tw:p-0 tw:m-0 tw:shrink-0"
                  size="icon-sm"
                  aria-label="Close"
                />
              }
            >
              <X />
            </DialogClose>
          </div>
          <p className="tw:m-0 tw:text-xs tw:leading-relaxed tw:text-[#523D33]/80">
            Choose how long to pause your subscription.
          </p>
        </DialogHeader>
        <fieldset className="tw:flex tw:flex-col tw:gap-3 tw:border-0 tw:p-0 tw:m-0 tw:min-w-0 tw:mt-2">
          {allowedMonths.map((m) => {
            const inputId = `pause-months-${m}`;
            const selected = selectedMonths === m;
            const resumeDate = pauseUntilMonthsFromToday(m);
            return (
              <label
                key={m}
                htmlFor={inputId}
                className={cn(
                  "tw:flex tw:cursor-pointer tw:items-center tw:gap-3 tw:rounded-md tw:border tw:p-4 tw:text-sm tw:transition-colors",
                  selected
                    ? "tw:border-[#523D33] tw:bg-[#FDEDEE]/50"
                    : "tw:border-gray-200 tw:bg-white hover:tw:bg-gray-50/80",
                )}
              >
                <input
                  id={inputId}
                  type="radio"
                  name="pause-months"
                  value={m}
                  checked={selected}
                  disabled={pauseIsPending}
                  onChange={() => setSelectedMonths(m)}
                  className="tw:size-4.5 tw:shrink-0 tw:accent-[#523D33] tw:cursor-pointer"
                />
                <div className="tw:flex tw:flex-col tw:flex-1 tw:min-w-0">
                  <span
                    className="tw:font-medium tw:leading-snug"
                    style={{ color: "var(--so-text, #523D33)" }}
                  >
                    {m} month{m > 1 ? "s" : ""}
                  </span>
                  <span className="tw:text-xs tw:text-[#523D33]/70 tw:leading-snug">
                    Resumes {formatSubscriptionDateForDisplay(resumeDate)}
                  </span>
                </div>
              </label>
            );
          })}
        </fieldset>
        <DialogFooter className="tw:flex-col tw:sm:flex-col tw:gap-2 tw:mt-4 tw:pt-2">
          <Button
            type="button"
            variant="outline"
            disabled={pauseIsPending || selectedMonths == null}
            onClick={() => {
              if (selectedMonths != null) onConfirmPauseMonths(selectedMonths);
            }}
            className={PILL_OUTLINE_CLASS}
          >
            {pauseIsPending ? (
              <Loader2 className="tw:size-4 tw:animate-spin tw:mx-auto" />
            ) : (
              "Pause"
            )}
          </Button>
          <Button
            type="button"
            disabled={pauseIsPending}
            onClick={onDismiss}
            className={PILL_FILLED_CLASS}
          >
            Never mind
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
